import { useState, useEffect, useCallback, lazy, Suspense } from 'react'
import {
  X, User, Shield, Bell, CreditCard, DollarSign, Crown, Upload,
  BarChart3, Wallet, Megaphone, Tag, Gift, ClipboardList, Download,
  ChevronDown, Star, MessageCircle, ShieldCheck, FileText, AlertTriangle,
  Flag, Search, Bot, Scale, Globe, Clock, Users, Headset,
  PenSquare, Calendar, Percent, Settings,
  Loader2
} from 'lucide-react'
import { useCommandModal } from '../../stores/commandModalStore'
import { useAuthStore } from '../../stores/authStore'
import { cn } from '../../lib/utils'

// Lazy-load the heavy panel components
const SettingsPage = lazy(() => import('../../pages/settings/SettingsPage'))
const CreatorDashboardPage = lazy(() => import('../../pages/dashboard/CreatorDashboardPage'))
const PlusPage = lazy(() => import('../../pages/plus/PlusPage'))
const PartnerPage = lazy(() => import('../../pages/partner/PartnerPage'))
const SupportPage = lazy(() => import('../../pages/admin/SupportPage'))
const ManagementPage = lazy(() => import('../../pages/admin/ManagementPage'))
const AdminPage = lazy(() => import('../../pages/admin/AdminPage'))

function PanelLoader() {
  return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="animate-spin text-zinc-600" size={24} />
    </div>
  )
}

// ── Sidebar components ──────────────────────────────────────────────────────

function MenuItem({ icon: Icon, label, active, badge, onClick }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-2.5 px-3 py-[7px] rounded-md text-[13px] transition-colors cursor-pointer text-left',
        active
          ? 'bg-white/[0.08] text-white font-medium'
          : 'text-zinc-400 hover:bg-white/[0.04] hover:text-zinc-300'
      )}
    >
      <Icon size={15} strokeWidth={active ? 2.2 : 1.7} className="flex-shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      {badge && (
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-semibold">
          {badge}
        </span>
      )}
    </button>
  )
}

function ExpandableMenuItem({ icon: Icon, label, expanded, onToggle, children }) {
  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2.5 px-3 py-[7px] rounded-md text-[13px] transition-colors cursor-pointer text-left text-zinc-400 hover:bg-white/[0.04] hover:text-zinc-300"
      >
        <Icon size={15} strokeWidth={1.7} className="flex-shrink-0" />
        <span className="flex-1 truncate">{label}</span>
        <ChevronDown size={13} className={cn('transition-transform duration-200 text-zinc-600', expanded && 'rotate-180')} />
      </button>
      {expanded && (
        <div className="ml-4 pl-2 border-l border-zinc-800/40 mt-0.5 space-y-0.5">
          {children}
        </div>
      )}
    </div>
  )
}

function MobileTab({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors cursor-pointer',
        active ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-zinc-300'
      )}
    >
      {label}
    </button>
  )
}

// ── Creator sub-sections (matches SettingsPage's creatorSections) ───────────

const CREATOR_SUBTABS = [
  { id: 'general', label: 'General', icon: Star },
  { id: 'pricing', label: 'Pricing', icon: DollarSign },
  { id: 'messaging', label: 'Messaging', icon: MessageCircle },
  { id: 'privacy', label: 'Privacy & Safety', icon: Shield },
  { id: 'partner', label: 'Partner', icon: ShieldCheck },
  { id: 'payout', label: 'Payouts', icon: CreditCard },
  { id: 'tax', label: 'Tax Info', icon: FileText },
  { id: 'danger', label: 'Danger Zone', icon: AlertTriangle },
]

// ── Tab definitions per modal ───────────────────────────────────────────────

const SETTINGS_TABS = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'account', label: 'Security', icon: Shield },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'creator', label: 'Creator', icon: CreditCard, expandable: true, children: CREATOR_SUBTABS },
  { id: 'billing', label: 'Billing', icon: DollarSign },
]

const DASHBOARD_TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'wallet', label: 'Wallet', icon: Wallet },
  { id: 'mass-message', label: 'Mass Message', icon: Megaphone },
  { id: 'promotions', label: 'Promotions', icon: Tag },
  { id: 'referrals', label: 'Referrals', icon: Gift },
  { id: 'requests', label: 'Requests', icon: ClipboardList },
  { id: 'ads', label: 'Ads', icon: Megaphone },
  { id: 'export', label: 'Export', icon: Download },
]

const SUPPORT_TABS = [
  { id: 'reports', label: 'Reports', icon: Flag },
  { id: 'users', label: 'Users', icon: Search },
  { id: 'automod', label: 'Auto-Mod', icon: Bot },
  { id: 'appeals', label: 'Appeals', icon: Scale },
  { id: 'ipbans', label: 'IP Bans', icon: Globe },
  { id: 'log', label: 'Mod Log', icon: Clock },
]

const SUPPORT_LEAD_TABS = [
  { id: 'team', label: 'Team', icon: Users },
  { id: 'partners', label: 'Partners', icon: Headset },
]

const MANAGEMENT_TABS = [
  { id: 'content', label: 'Content Queue', icon: Upload },
  { id: 'post', label: 'Post as Creator', icon: PenSquare },
  { id: 'scheduled', label: 'Scheduled', icon: Calendar },
]

const ADMIN_TABS = [
  { id: 'revenue', label: 'Revenue', icon: DollarSign },
  { id: 'payouts', label: 'Payouts', icon: Wallet },
  { id: 'splits', label: 'Splits', icon: Percent },
  { id: 'roles', label: 'Roles', icon: Settings },
  { id: 'partners', label: 'Partners', icon: ShieldCheck },
  { id: 'ads', label: 'Ads', icon: Megaphone },
  { id: 'plus', label: 'Plus', icon: Crown },
]

const MODAL_CONFIG = {
  settings: { title: 'Settings', tabs: SETTINGS_TABS },
  dashboard: { title: 'Creator Dashboard', tabs: DASHBOARD_TABS },
  plus: { title: 'Heatly+', tabs: null },
  partner: { title: 'Partner Program', tabs: null },
  support: { title: 'Support', tabs: SUPPORT_TABS },
  management: { title: 'Management', tabs: MANAGEMENT_TABS },
  admin: { title: 'Admin', tabs: ADMIN_TABS },
}

// ── Sidebar with expandable groups ──────────────────────────────────────────

function SidebarNav({ tabs, activeTab, onTabChange, expandedGroups, toggleGroup }) {
  return (
    <nav className="flex-1 overflow-y-auto custom-scrollbar px-2 py-1 space-y-0.5">
      {tabs.map(t => {
        if (t.expandable && t.children) {
          const isExpanded = expandedGroups[t.id] || activeTab.startsWith(t.id + ':')
          const isChildActive = activeTab.startsWith(t.id + ':')
          return (
            <ExpandableMenuItem
              key={t.id}
              icon={t.icon}
              label={t.label}
              expanded={isExpanded}
              onToggle={() => toggleGroup(t.id)}
            >
              {t.children.map(child => (
                <MenuItem
                  key={child.id}
                  icon={child.icon}
                  label={child.label}
                  badge={child.badge}
                  active={activeTab === `${t.id}:${child.id}`}
                  onClick={() => onTabChange(`${t.id}:${child.id}`)}
                />
              ))}
            </ExpandableMenuItem>
          )
        }
        return (
          <MenuItem
            key={t.id}
            icon={t.icon}
            label={t.label}
            badge={t.badge}
            active={activeTab === t.id}
            onClick={() => onTabChange(t.id)}
          />
        )
      })}
    </nav>
  )
}

// ── Generic Modal Shell ─────────────────────────────────────────────────────

function ModalShell({ title, tabs, activeTab, onTabChange, onClose, expandedGroups, toggleGroup, children }) {
  const hasSidebar = tabs && tabs.length > 0

  // Flat list for mobile (expand children inline)
  const flatTabs = tabs ? tabs.flatMap(t =>
    t.expandable && t.children
      ? t.children.map(c => ({ ...c, id: `${t.id}:${c.id}`, parentLabel: t.label }))
      : [t]
  ) : []

  // Find display label for current tab
  const findLabel = (tabId) => {
    for (const t of (tabs || [])) {
      if (t.id === tabId) return t.label
      if (t.children) {
        const child = t.children.find(c => `${t.id}:${c.id}` === tabId)
        if (child) return child.label
      }
    }
    return title
  }

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className={cn(
        'relative w-full h-full bg-[#0f0f0f] overflow-hidden flex shadow-2xl shadow-black/50 glow-md animate-in fade-in zoom-in-[0.98] duration-150 border border-zinc-800/60',
        hasSidebar
          ? 'md:w-[min(1100px,94vw)] md:h-[min(720px,88vh)] md:rounded-xl'
          : 'md:w-[min(720px,90vw)] md:h-[min(680px,85vh)] md:rounded-xl'
      )}>

        {/* ── Left Sidebar ──────────────────────────────────────── */}
        {hasSidebar && (
          <div className="hidden md:flex flex-col w-[200px] flex-shrink-0 bg-[#0a0a0a] border-r border-zinc-800/40">
            <div className="px-4 pt-4 pb-3">
              <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{title}</p>
            </div>
            <SidebarNav
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={onTabChange}
              expandedGroups={expandedGroups}
              toggleGroup={toggleGroup}
            />
          </div>
        )}

        {/* ── Content Area ──────────────────────────────────────── */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Top bar */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-800/40 flex-shrink-0">
            {/* Mobile tab bar */}
            {hasSidebar ? (
              <div className="flex md:hidden items-center gap-1 overflow-x-auto flex-1 mr-2">
                {flatTabs.map(t => (
                  <MobileTab
                    key={t.id}
                    label={t.label}
                    active={activeTab === t.id}
                    onClick={() => onTabChange(t.id)}
                  />
                ))}
              </div>
            ) : (
              <h2 className="text-sm font-semibold text-zinc-300 md:hidden">{title}</h2>
            )}

            {/* Desktop: current tab label */}
            <h2 className="hidden md:block text-sm font-semibold text-zinc-300">
              {findLabel(activeTab)}
            </h2>

            <button
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors cursor-pointer flex-shrink-0"
            >
              <X size={16} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            <Suspense fallback={<PanelLoader />}>
              {children}
            </Suspense>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Main Export ──────────────────────────────────────────────────────────────

export default function CommandModal() {
  const { activeModal, activeTab, extra, close, setTab } = useCommandModal()
  const { profile } = useAuthStore()
  const [expandedGroups, setExpandedGroups] = useState({})

  const toggleGroup = useCallback((groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }))
  }, [])

  // Auto-expand group when a child tab is active
  useEffect(() => {
    if (activeTab && activeTab.includes(':')) {
      const groupId = activeTab.split(':')[0]
      setExpandedGroups(prev => ({ ...prev, [groupId]: true }))
    }
  }, [activeTab])

  // Escape key closes modal
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') close()
  }, [close])

  useEffect(() => {
    if (activeModal) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [activeModal, handleKeyDown])

  if (!activeModal) return null

  const config = MODAL_CONFIG[activeModal]
  if (!config) return null

  // Build tabs list with dynamic additions
  let tabs = config.tabs ? [...config.tabs] : null

  // Settings: add management tab if user is managed
  if (activeModal === 'settings' && profile?.is_managed) {
    tabs = [...tabs, { id: 'management', label: 'Management', icon: Upload }]
  }
  // Settings: only show creator expandable if user is creator
  if (activeModal === 'settings' && tabs) {
    tabs = tabs.map(t => {
      if (t.id === 'creator' && !profile?.is_creator) return { ...t, expandable: false, children: undefined }
      // Filter partner child tab for non-creators
      if (t.id === 'creator' && t.children && profile?.is_creator) {
        return {
          ...t,
          children: t.children.filter(c => c.id !== 'partner' || profile?.is_creator),
        }
      }
      return t
    })
  }
  // Support: add lead tabs if user is lead/admin
  if (activeModal === 'support') {
    const isLeadOrAdmin = profile?.system_role === 'admin' || profile?.system_role === 'support_lead'
    if (isLeadOrAdmin) {
      tabs = [...tabs, ...SUPPORT_LEAD_TABS]
    }
  }

  // Determine what to pass to embedded pages
  const resolveSettingsTab = () => {
    if (!activeTab) return 'profile'
    if (activeTab.startsWith('creator:')) return 'creator'
    return activeTab
  }

  const resolveCreatorSection = () => {
    if (activeTab && activeTab.startsWith('creator:')) return activeTab.split(':')[1]
    return undefined
  }

  return (
    <ModalShell
      title={config.title}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setTab}
      onClose={close}
      expandedGroups={expandedGroups}
      toggleGroup={toggleGroup}
    >
      {activeModal === 'settings' && (
        <SettingsPage embedded defaultTab={resolveSettingsTab()} creatorSection={resolveCreatorSection()} />
      )}
      {activeModal === 'dashboard' && (
        <CreatorDashboardPage embedded defaultTab={activeTab} extraData={extra} />
      )}
      {activeModal === 'plus' && (
        <PlusPage embedded />
      )}
      {activeModal === 'partner' && (
        <PartnerPage embedded />
      )}
      {activeModal === 'support' && (
        <SupportPage embedded defaultTab={activeTab} />
      )}
      {activeModal === 'management' && (
        <ManagementPage embedded defaultTab={activeTab} />
      )}
      {activeModal === 'admin' && (
        <AdminPage embedded defaultTab={activeTab} />
      )}
    </ModalShell>
  )
}
